import { useState } from "react";
import { format, parseISO } from "date-fns";
import { type Appointment } from "@/types";
import AddAppointmentModal from "@/components/Appointment/AddAppointmentModal";
import EditAppointmentModal from "@/components/Appointment/EditAppointmentModal";
import { Button } from "@/components/ui/button";
import { Calendar, Plus, Clock, Edit, CheckCircle, XCircle, UserCheck } from "lucide-react";

type Props = {
  appointments: Appointment[];
  patientId: string;
  patientName?: string;
  onRefresh?: () => void;
};

const STATUS_STYLE: Record<string, { icon: React.ReactNode; badge: string }> = {
  completed: {
    icon: <CheckCircle className="w-4 h-4 text-success" />,
    badge: 'bg-success/10 text-success border-success/20',
  },
  cancelled: {
    icon: <XCircle className="w-4 h-4 text-destructive" />,
    badge: 'bg-destructive/10 text-destructive border-destructive/20',
  },
  no_show: {
    icon: <XCircle className="w-4 h-4 text-destructive" />,
    badge: 'bg-destructive/10 text-destructive border-destructive/20',
  },
  checked_in: {
    icon: <UserCheck className="w-4 h-4 text-warning" />,
    badge: 'bg-warning/10 text-warning border-warning/20',
  },
  default: {
    icon: <Clock className="w-4 h-4 text-primary" />,
    badge: 'bg-primary/10 text-primary border-primary/20',
  },
};

function statusStyle(status: string) {
  return STATUS_STYLE[status?.toLowerCase()] ?? STATUS_STYLE.default;
}

export default function AppointmentsTab({ appointments, patientId, patientName, onRefresh }: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Appointment | null>(null);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Calendar className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Appointments</h2>
            <p className="text-sm text-muted-foreground">
              {appointments?.length || 0} appointment{(appointments?.length || 0) !== 1 ? 's' : ''}
              {appointments?.length > 0 && (() => {
                const today = new Date().toISOString().split('T')[0];
                const next = appointments
                  .filter(a => a.status === 'scheduled' && (a.date ?? '') >= today)
                  .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))[0]
                  ?.date?.split('T')[0];
                if (!next) return null;
                const [y, m, day] = next.split('-').map(Number);
                return <> · Next: {format(new Date(y, m - 1, day), 'd MMM yyyy')}</>;
              })()}
            </p>
          </div>
        </div>
        <Button onClick={() => setAddOpen(true)} className="medical-button-primary">
          <Plus className="w-4 h-4 mr-2" /> New Appointment
        </Button>
      </div>

      {/* List */}
      {appointments?.length > 0 ? (
        <div className="space-y-3">
          {appointments.map(apt => {
            const { icon, badge } = statusStyle(apt.status);
            const statusLabel = (apt.status || 'scheduled').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
            return (
              <div key={apt.id} className="bg-card border border-border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {(() => { const d = apt.date?.split('T')[0]; if (!d) return '—'; const [y,m,day] = d.split('-').map(Number); return format(new Date(y, m-1, day), 'd MMMM yyyy'); })()}
                      </span>
                      {apt.time && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground ml-1">
                          <Clock className="w-3 h-3" /> {apt.time}
                        </span>
                      )}
                    </div>
                    {apt.reason && (
                      <p className="text-sm text-foreground font-medium mb-2">{apt.reason}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {icon}
                      <span className={`text-xs px-2 py-1 rounded border font-medium ${badge}`}>
                        {statusLabel}
                      </span>
                      {apt.doctor_name && (
                        <span className="text-xs text-muted-foreground">
                          · {apt.doctor_name.startsWith('Dr.') ? apt.doctor_name : `Dr. ${apt.doctor_name}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setEditing(apt)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 bg-muted/30 rounded-lg border border-border">
          <Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-sm font-medium text-foreground mb-1">No appointments scheduled</h3>
          <p className="text-xs text-muted-foreground">Schedule the first appointment for this patient</p>
        </div>
      )}

      <AddAppointmentModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        patientId={patientId}
        showPatientSelection={false}
        title="Schedule Appointment"
        description={`Schedule a new appointment for ${patientName || 'this patient'}`}
        onAdded={() => { setAddOpen(false); onRefresh?.(); }}
      />

      {editing && (
        <EditAppointmentModal
          isOpen
          appointment={editing}
          onClose={() => setEditing(null)}
          onUpdated={() => { setEditing(null); onRefresh?.(); }}
        />
      )}
    </div>
  );
}
